import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import Footer from '../components/Footer';
import { loadState, updatePage } from '../utils/stateManager';
import './Page3VMConfig.css';

function Page3VMConfig() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    environment: 'production',
    region: 'eastus',
    vmSize: 'Standard_B2s',
    purpose: 'app',
    vmFunction: '',
    vmPurpose: '',
    instance: '01',
    adminUser: 'vmadmin',
    vmNameOverride: '',
    useAutoName: true
  });

  const [derivedVMName, setDerivedVMName] = useState('');
  const [finalVMName, setFinalVMName] = useState('');
  const [vmNameValid, setVmNameValid] = useState(true);

  useEffect(() => {
    const state = loadState();
    if (state.page3VMConfig) {
      setFormData(state.page3VMConfig);
    }
  }, []);

  useEffect(() => {
    // Generate derived VM name (includes region code to match backend)
    const envCode = formData.environment.substring(0, 3);

    // Get region code
    const regionCodes = {
      'westus2': 'az1',
      'eastus': 'az2',
      'centralus': 'az3'
    };
    const regionCode = regionCodes[formData.region] || 'az2';

    const derived = `${envCode}${regionCode}${formData.vmFunction}${formData.vmPurpose}${formData.instance}`;
    setDerivedVMName(derived);

    // Determine final VM name (override or derived)
    const final = formData.useAutoName ? derived : formData.vmNameOverride;
    setFinalVMName(final);

    // Validate
    setVmNameValid(final.length > 0 && final.length <= 15);

    // Update state
    const updated = { ...formData, derivedVMName: derived, finalVMName: final };
    updatePage('page3VMConfig', updated);
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleOverride = () => {
    const newUseAuto = !formData.useAutoName;
    setFormData({
      ...formData,
      useAutoName: newUseAuto,
      vmNameOverride: newUseAuto ? '' : derivedVMName
    });
  };

  const handleBack = () => {
    navigate('/page2');
  };

  const handleContinue = () => {
    if (!formData.vmFunction || !formData.vmPurpose || !finalVMName || !vmNameValid) {
      alert('Please fill in all required fields with valid values');
      return;
    }
    navigate('/page4');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={3} totalPages={9} />
      </div>

      <div className="page-content">
        <h2>Step 3: Configure Virtual Machine</h2>

        <div className="form-section">
          <div className="form-group">
            <label>Environment:</label>
            <select value={formData.environment} onChange={(e) => handleChange('environment', e.target.value)}>
              <option value="development">Development</option>
              <option value="testing">Testing</option>
              <option value="staging">Staging</option>
              <option value="production">Production</option>
            </select>
          </div>

          <div className="form-group">
            <label>Azure Region:</label>
            <select value={formData.region} onChange={(e) => handleChange('region', e.target.value)}>
              <option value="westus2">West US 2 (az1)</option>
              <option value="eastus">East US (az2)</option>
              <option value="centralus">Central US (az3)</option>
            </select>
          </div>

          <div className="form-group">
            <label>VM Size:</label>
            <select value={formData.vmSize} onChange={(e) => handleChange('vmSize', e.target.value)}>
              <option value="Standard_B2s">Standard_B2s (2 vCPU, 4GB) ~$30/mo</option>
              <option value="Standard_B2ms">Standard_B2ms (2 vCPU, 8GB) ~$60/mo</option>
              <option value="Standard_D2s_v3">Standard_D2s_v3 (2 vCPU, 8GB) ~$70/mo</option>
              <option value="Standard_D4s_v3">Standard_D4s_v3 (4 vCPU, 16GB) ~$140/mo</option>
            </select>
          </div>

          <div className="form-group">
            <label>Workload Type (for tagging):</label>
            <select value={formData.purpose} onChange={(e) => handleChange('purpose', e.target.value)}>
              <option value="mfg">Manufacturing</option>
              <option value="ops">Operations</option>
              <option value="eng">Engineering</option>
              <option value="app">Application (Default)</option>
              <option value="fin">Finance</option>
              <option value="dba">Database</option>
              <option value="web">Web Services</option>
              <option value="api">API Services</option>
              <option value="rpt">Reporting</option>
              <option value="sec">Security</option>
              <option value="net">Networking</option>
              <option value="mon">Monitoring</option>
            </select>
            <small>Will be added as 'Workload' tag on Page 7. Not used in VM name.</small>
          </div>

          <div className="form-group">
            <label>VM Function:</label>
            <input
              type="text"
              value={formData.vmFunction}
              onChange={(e) => handleChange('vmFunction', e.target.value)}
              placeholder="e.g., mfg, web, sql"
              maxLength="6"
            />
            <small>What the VM does (e.g., mfg, web, sql, app)</small>
          </div>

          <div className="form-group">
            <label>VM Purpose:</label>
            <input
              type="text"
              value={formData.vmPurpose}
              onChange={(e) => handleChange('vmPurpose', e.target.value)}
              placeholder="e.g., app, db, api"
              maxLength="6"
            />
            <small>Purpose detail (e.g., app, db, api, srv)</small>
          </div>

          <div className="form-group">
            <label>Instance Number:</label>
            <input
              type="text"
              value={formData.instance}
              onChange={(e) => handleChange('instance', e.target.value)}
              placeholder="01"
              maxLength="2"
            />
            <small>Instance number (01, 02, 03...)</small>
          </div>

          <div className="form-group">
            <label>Admin Username:</label>
            <input
              type="text"
              value={formData.adminUser}
              onChange={(e) => handleChange('adminUser', e.target.value)}
              placeholder="vmadmin"
            />
          </div>
        </div>

        <div className="vm-name-section">
          <h3>VM Name</h3>

          <div className="derived-name">
            <label>Auto-Generated Name:</label>
            <div className="name-display">
              <code>{derivedVMName || '(fill in fields above)'}</code>
              <span className="char-count">{derivedVMName.length}/15</span>
            </div>
          </div>

          <div className="override-toggle">
            <label>
              <input
                type="checkbox"
                checked={!formData.useAutoName}
                onChange={toggleOverride}
              />
              Override VM Name
            </label>
          </div>

          {!formData.useAutoName && (
            <div className="form-group">
              <label>Custom VM Name:</label>
              <input
                type="text"
                value={formData.vmNameOverride}
                onChange={(e) => handleChange('vmNameOverride', e.target.value)}
                placeholder="Enter custom name (15 char max)"
                maxLength="15"
              />
            </div>
          )}

          <div className={`final-name-display ${vmNameValid ? 'valid' : 'invalid'}`}>
            <div className="final-name-label">Final VM Name:</div>
            <div className="final-name-value">
              <strong>{finalVMName || '(incomplete)'}</strong>
              <span className="validation-icon">{vmNameValid && finalVMName ? '✅' : '❌'}</span>
            </div>
            {!vmNameValid && finalVMName && (
              <div className="error-message">Exceeds 15 character limit ({finalVMName.length} chars)</div>
            )}
            {!finalVMName && (
              <div className="error-message">VM name cannot be empty</div>
            )}
          </div>
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button
          className="btn-primary"
          onClick={handleContinue}
          disabled={!vmNameValid || !finalVMName}
        >
          Next: KeyVault →
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page3VMConfig;
